import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  X, ExternalLink, Activity, AlertTriangle, FileText, Mail, Flag,
  Image as ImageIcon, Calendar, User, CheckCircle, AlertCircle, Clock, Trash2,
} from "lucide-react";
import { portalApi } from "../../lib/portalApi";

const KIND_META = {
  active: {
    title: "Active Projects",
    eyebrow: "Active & in planning",
    icon: Activity,
    accent: "maroon",
    empty: "No active or planning projects right now.",
  },
  delayed: {
    title: "Delayed / At Risk",
    eyebrow: "Projects + tasks needing attention",
    icon: AlertTriangle,
    accent: "red",
    empty: "Nothing delayed or at risk — all on track.",
  },
  "reports-pending": {
    title: "Reports Pending",
    eyebrow: "Drafts awaiting send",
    icon: FileText,
    accent: "gold",
    empty: "No draft reports waiting to be sent.",
  },
  responses: {
    title: "Pending Client Responses",
    eyebrow: "Response/approval-required reports awaiting reply",
    icon: Mail,
    accent: "maroon",
    empty: "No outstanding client responses. Information-only reports are not tracked here.",
  },
  milestones: {
    title: "Upcoming Milestones",
    eyebrow: "Due in the next 30 days",
    icon: Flag,
    accent: "gold",
    empty: "No milestones due in the next 30 days.",
  },
  uploads: {
    title: "Recent Uploads",
    eyebrow: "Photos uploaded in the last 7 days",
    icon: ImageIcon,
    accent: "charcoal",
    empty: "No photos uploaded in the last 7 days.",
  },
};

const ACCENT_STYLES = {
  maroon: { bar: "bg-[#6E1F2C]", text: "text-[#6E1F2C]", soft: "bg-[#6E1F2C]/10" },
  gold: { bar: "bg-[#D4A963]", text: "text-[#A6822F]", soft: "bg-[#D4A963]/15" },
  red: { bar: "bg-[#A3141A]", text: "text-[#A3141A]", soft: "bg-[#A3141A]/10" },
  charcoal: { bar: "bg-[#1C110C]", text: "text-[#1C110C]", soft: "bg-[#1C110C]/10" },
};

const STATUS_PILL = {
  active: { l: "Active", cls: "bg-[#6E1F2C] text-white" },
  planning: { l: "Planning", cls: "bg-[#7A6B5F] text-white" },
  delayed: { l: "Delayed", cls: "bg-[#A3141A] text-white" },
  completed: { l: "Completed", cls: "bg-[#3F6E2C] text-white" },
  in_progress: { l: "In Progress", cls: "bg-[#6E1F2C] text-white" },
  not_started: { l: "Not Started", cls: "bg-[#7A6B5F] text-white" },
  on_hold: { l: "On Hold", cls: "bg-[#56151F] text-white" },
  awaiting_approval: { l: "Awaiting Approval", cls: "bg-[#D4A963] text-[#1C110C]" },
  awaiting_client: { l: "Awaiting Client", cls: "bg-[#D4A963] text-[#1C110C]" },
  handover: { l: "Handover", cls: "bg-[#56151F] text-white" },
  draft: { l: "Draft", cls: "bg-[#7A6B5F] text-white" },
  planned: { l: "Planned", cls: "bg-[#7A6B5F] text-white" },
  blocked: { l: "Blocked", cls: "bg-[#1C110C] text-white" },
};

const RESP_GROUP_LABELS = {
  project_manager: "Project Manager",
  site_engineer: "Site Engineer",
  admin_staff: "Admin Staff",
  client: "Client",
  client_project_manager: "Client PM",
  client_interior_designer: "Client Designer",
  client_associate: "Client Associate",
  client_subcontractor: "Client Subcontractor",
  client_supplier: "Client Supplier",
  subcontractor: "Subcontractor",
  supplier: "Supplier",
  delivery_team: "Delivery",
  housekeeping_team: "Housekeeping",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}
function daysDelta(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso.slice(0, 10));
    return Math.round((d - new Date()) / 86400000);
  } catch { return null; }
}

function StatusPill({ status }) {
  const s = STATUS_PILL[status] || { l: status || "—", cls: "bg-[#7A6B5F] text-white" };
  return (
    <span className={`text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 ${s.cls}`}>
      {s.l}
    </span>
  );
}

/* ---------- Per-kind row renderers ---------- */

function ActiveRow({ it, onClose }) {
  const d = daysDelta(it.target_date);
  const overdue = d !== null && d < 0;
  return (
    <Link
      to={`/projects/${it.project_slug}`}
      onClick={onClose}
      data-testid={`portal-kpi-row-active-${it.project_slug}`}
      className="group flex items-center gap-4 bg-white border border-[#6E1F2C]/12 hover:border-[#6E1F2C]/40 hover:shadow-[0_8px_24px_-12px_rgba(110,31,44,0.25)] p-4 transition-all"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <StatusPill status={it.status} />
          {it.project_code && (
            <span className="text-[9px] tracking-[0.2em] uppercase text-[#7A6B5F] font-mono">
              {it.project_code}
            </span>
          )}
        </div>
        <h4 className="font-serif-display text-base text-[#1C110C] group-hover:text-[#6E1F2C] truncate">
          {it.project_name}
        </h4>
        <p className="text-[11px] text-[#7A6B5F] truncate">
          {[it.client, it.location].filter(Boolean).join(" · ") || "—"}
        </p>
        {it.next_milestone_name && (
          <p className="text-[10px] text-[#3E2A22] mt-1 truncate">
            <Flag size={9} className="inline mr-1 text-[#A6822F]" />
            Next: {it.next_milestone_name} · {fmtDate(it.next_milestone_date)}
          </p>
        )}
      </div>
      <div className="text-right flex flex-col items-end gap-1 shrink-0">
        <div className="font-serif-display text-lg text-[#6E1F2C] leading-none">{it.progress_percent || 0}%</div>
        {it.target_date && (
          <span className={`text-[10px] tracking-[0.18em] uppercase ${overdue ? "text-[#A3141A]" : d <= 30 ? "text-[#6E1F2C]" : "text-[#7A6B5F]"}`}>
            {overdue ? `${Math.abs(d)} d overdue` : `${d} d left`}
          </span>
        )}
        <ExternalLink size={12} className="text-[#7A6B5F] group-hover:text-[#6E1F2C]" />
      </div>
    </Link>
  );
}

function DelayedRow({ it, onClose }) {
  const isTask = it.type === "task";
  const to = isTask
    ? `/projects/${it.project_slug}?tab=timeline`
    : `/projects/${it.project_slug}`;
  return (
    <Link
      to={to}
      onClick={onClose}
      data-testid={`portal-kpi-row-delayed-${it.type}-${it.task_id || it.project_slug}`}
      className="group flex items-center gap-3 bg-white border border-[#A3141A]/20 hover:border-[#A3141A]/55 p-4 transition-all"
    >
      <div className="w-1 self-stretch bg-[#A3141A]" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <StatusPill status={it.status} />
          <span className="text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 bg-[#A3141A]/10 text-[#A3141A] border border-[#A3141A]/30">
            {(it.risk || "high").toUpperCase()}
          </span>
          <span className="text-[9px] tracking-[0.22em] uppercase text-[#7A6B5F]">{it.label}</span>
        </div>
        <h4 className="font-serif-display text-base text-[#1C110C] group-hover:text-[#A3141A] truncate">
          {isTask ? it.task_name : it.project_name}
        </h4>
        <p className="text-[11px] text-[#7A6B5F] truncate">
          {isTask ? `${it.project_name}${it.project_code ? ` · ${it.project_code}` : ""}` : (it.detail || "—")}
        </p>
        {it.target_date && (
          <p className="text-[10px] text-[#3E2A22] mt-1">
            <Calendar size={9} className="inline mr-1" /> {isTask ? "Finish" : "Target"} {fmtDate(it.target_date)}
          </p>
        )}
      </div>
      <ExternalLink size={12} className="text-[#7A6B5F] group-hover:text-[#A3141A] shrink-0" />
    </Link>
  );
}

function ReportPendingRow({ it, onClose, onChanged }) {
  const [busy, setBusy] = React.useState(false);
  const discard = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`Discard this ${it.kind} draft for ${it.project_name}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await portalApi.discardReport(it.project_slug, it.report_id);
      onChanged && onChanged();
    } catch { /* ignore */ }
    finally { setBusy(false); }
  };
  return (
    <Link
      to={`/projects/${it.project_slug}?tab=reports&report=${it.report_id}`}
      onClick={onClose}
      data-testid={`portal-kpi-row-reports-pending-${it.report_id}`}
      className="group flex items-center gap-4 bg-white border border-[#D4A963]/30 hover:border-[#D4A963] p-4 transition-all"
    >
      <div className={`w-1.5 self-stretch ${it.kind === "weekly" ? "bg-[#D4A963]" : "bg-[#6E1F2C]"}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className={`text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 ${it.kind === "weekly" ? "bg-[#D4A963] text-[#1C110C]" : "bg-[#6E1F2C] text-white"}`}>
            {it.kind === "weekly" ? "Weekly" : "Daily"}
          </span>
          {it.automation ? (
            <span className="text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 bg-[#D4A963]/20 text-[#A6822F] border border-[#D4A963]/50 inline-flex items-center gap-1">
              <Clock size={9} /> Awaiting approval
            </span>
          ) : (
            <StatusPill status="draft" />
          )}
          {it.approved && (
            <span className="text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 bg-[#3F6E2C]/10 text-[#3F6E2C] border border-[#3F6E2C]/30 inline-flex items-center gap-1">
              <CheckCircle size={9} /> Approved
            </span>
          )}
          {it.include_financials && (
            <span className="text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 bg-[#3F6E2C]/15 text-[#3F6E2C] border border-[#3F6E2C]/30">
              Financials
            </span>
          )}
          <span className="text-[9px] tracking-[0.18em] uppercase text-[#7A6B5F]">{it.audience}</span>
        </div>
        <h4 className="font-serif-display text-base text-[#1C110C] group-hover:text-[#6E1F2C] truncate">
          {it.project_name}
        </h4>
        <p className="text-[11px] text-[#7A6B5F] truncate">
          {it.subject || "Untitled report"} · period ends {fmtDate(it.period_end)}
        </p>
      </div>
      <div className="flex flex-col gap-1 items-end shrink-0">
        <span className="text-[9px] tracking-[0.22em] uppercase px-2 py-1 bg-[#3F6E2C] text-white group-hover:bg-[#2F5520] inline-flex items-center gap-1">
          <CheckCircle size={10} /> Review &amp; send
        </span>
        <button
          type="button"
          onClick={discard}
          disabled={busy}
          data-testid={`portal-kpi-row-discard-${it.report_id}`}
          className="text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 text-[#A3141A] hover:text-white hover:bg-[#A3141A] border border-[#A3141A]/40 inline-flex items-center gap-1 disabled:opacity-50"
          title="Discard draft"
        >
          <Trash2 size={9} /> Discard
        </button>
      </div>
    </Link>
  );
}

function ResponseRow({ it, onClose }) {
  return (
    <Link
      to={`/projects/${it.project_slug}?tab=reports&report=${it.report_id}`}
      onClick={onClose}
      data-testid={`portal-kpi-row-responses-${it.report_id}`}
      className="group flex items-center gap-4 bg-white border border-[#6E1F2C]/15 hover:border-[#6E1F2C]/55 p-4 transition-all"
    >
      <div className="w-9 h-9 bg-[#6E1F2C]/10 text-[#6E1F2C] flex items-center justify-center shrink-0">
        <Mail size={15} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className={`text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 ${it.kind === "weekly" ? "bg-[#D4A963] text-[#1C110C]" : "bg-[#6E1F2C] text-white"}`}>
            {it.kind}
          </span>
          <span className="text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 bg-[#D4A963]/15 text-[#A6822F] border border-[#D4A963]/40 inline-flex items-center gap-1">
            <AlertCircle size={9} /> Awaiting reply
          </span>
        </div>
        <h4 className="font-serif-display text-base text-[#1C110C] group-hover:text-[#6E1F2C] truncate">
          {it.project_name}
        </h4>
        <p className="text-[11px] text-[#7A6B5F] truncate">
          To {it.recipients_count} recipient{it.recipients_count === 1 ? "" : "s"}
          {it.recipients_preview ? ` · ${it.recipients_preview}` : ""} · sent {fmtDate(it.sent_at)}
        </p>
      </div>
      <ExternalLink size={12} className="text-[#7A6B5F] group-hover:text-[#6E1F2C] shrink-0" />
    </Link>
  );
}

function MilestoneRow({ it, onClose }) {
  const d = daysDelta(it.target_date);
  const respL = RESP_GROUP_LABELS[it.responsible_group];
  const depL = RESP_GROUP_LABELS[it.dependency_group];
  return (
    <Link
      to={`/projects/${it.project_slug}?tab=milestones`}
      onClick={onClose}
      data-testid={`portal-kpi-row-milestones-${it.milestone_id}`}
      className="group flex items-center gap-3 bg-white border border-[#D4A963]/30 hover:border-[#D4A963] p-4 transition-all"
    >
      <div className="w-9 h-9 bg-[#D4A963]/15 text-[#A6822F] flex items-center justify-center shrink-0">
        <Flag size={15} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <StatusPill status={it.status} />
          {it.is_next_milestone && (
            <span className="text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 bg-[#D4A963] text-[#1C110C]">
              ★ Next week
            </span>
          )}
        </div>
        <h4 className="font-serif-display text-base text-[#1C110C] group-hover:text-[#6E1F2C] truncate">
          {it.name}
        </h4>
        <p className="text-[11px] text-[#7A6B5F] truncate">
          {it.project_name} · {fmtDate(it.target_date)}
          {d !== null && ` · in ${d} d`}
        </p>
        {(respL || depL) && (
          <p className="text-[10px] text-[#3E2A22] mt-1 truncate">
            {respL && <><User size={9} className="inline mr-1" />{respL}</>}
            {depL && <span className="ml-2 text-[#7A6B5F]">↳ depends on {depL}</span>}
          </p>
        )}
      </div>
      <div className="text-right shrink-0">
        <div className="text-[11px] text-[#6E1F2C] font-medium">{it.progress_percent}%</div>
        <ExternalLink size={12} className="text-[#7A6B5F] group-hover:text-[#6E1F2C] mt-1" />
      </div>
    </Link>
  );
}

function UploadRow({ it, onClose }) {
  return (
    <Link
      to={`/projects/${it.project_slug}?tab=photos`}
      onClick={onClose}
      data-testid={`portal-kpi-row-uploads-${it.attachment_id}`}
      className="group flex items-center gap-3 bg-white border border-[#6E1F2C]/12 hover:border-[#6E1F2C]/40 p-3 transition-all"
    >
      <div className="w-14 h-14 bg-[#FAF5EC] border border-[#6E1F2C]/10 overflow-hidden flex items-center justify-center shrink-0">
        {it.url ? (
          <img src={it.url} alt={it.caption || it.filename || "photo"} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <ImageIcon size={18} className="text-[#7A6B5F]" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-serif-display text-sm text-[#1C110C] group-hover:text-[#6E1F2C] truncate">
          {it.caption || it.filename || "Photo"}
        </h4>
        <p className="text-[11px] text-[#7A6B5F] truncate">{it.project_name}</p>
        <p className="text-[10px] text-[#3E2A22] truncate">
          by {it.uploaded_by_name}
          {it.uploaded_by_role ? ` · ${it.uploaded_by_role.replace(/_/g, " ")}` : ""}
          {it.uploaded_at ? ` · ${fmtDate(it.uploaded_at)}` : ""}
        </p>
      </div>
      <ExternalLink size={12} className="text-[#7A6B5F] group-hover:text-[#6E1F2C] shrink-0" />
    </Link>
  );
}

const ROW_RENDERER = {
  active: ActiveRow,
  delayed: DelayedRow,
  "reports-pending": ReportPendingRow,
  responses: ResponseRow,
  milestones: MilestoneRow,
  uploads: UploadRow,
};

/* ---------- Drawer shell ---------- */

export default function KpiFilterDrawer({ kind, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [cleanBusy, setCleanBusy] = useState(false);
  const [cleanMsg, setCleanMsg] = useState("");

  const runCleanup = async () => {
    if (!window.confirm(
      "Convert all previously-sent reports without an explicit response/approval requirement to 'Sent · Information Only'?\n\nThey will no longer appear as Pending Client Responses. This cannot be undone."
    )) return;
    setCleanBusy(true); setCleanMsg("");
    try {
      const res = await portalApi.normalizePendingReports();
      setCleanMsg(`Cleaned up ${res.normalized_sent_reports} sent report${res.normalized_sent_reports === 1 ? "" : "s"}.`);
      setRefreshKey((k) => k + 1);
    } catch (e) { setCleanMsg(e.message || "Cleanup failed."); }
    finally { setCleanBusy(false); }
  };

  useEffect(() => {
    if (!kind) return undefined;
    let alive = true;
    portalApi
      .adminSummaryList(kind)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setErr(e.message || "Failed to load."); });
    return () => { alive = false; };
  }, [kind, refreshKey]);

  // Lock body scroll while drawer open + ESC closes.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  if (!kind) return null;
  const meta = KIND_META[kind];
  if (!meta) return null;
  const Icon = meta.icon;
  const accent = ACCENT_STYLES[meta.accent] || ACCENT_STYLES.maroon;
  const Row = ROW_RENDERER[kind];

  return (
    <div
      data-testid={`portal-kpi-drawer-${kind}`}
      className="fixed inset-0 z-[60] bg-[#1C110C]/60 backdrop-blur-sm flex justify-end"
      onClick={onClose}
    >
      <aside
        className="bg-[#FAF5EC] w-full max-w-[640px] h-full overflow-y-auto shadow-[0_0_60px_-10px_rgba(0,0,0,0.4)] border-l border-[#6E1F2C]/15"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`relative ${accent.soft} px-6 py-5 border-b border-[#6E1F2C]/15`}>
          <div className={`absolute top-0 left-0 right-0 h-[3px] ${accent.bar}`} />
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 ${accent.bar} text-white flex items-center justify-center`}>
                <Icon size={18} strokeWidth={1.8} />
              </div>
              <div>
                <p className={`text-[10px] tracking-[0.32em] uppercase ${accent.text} font-medium`}>
                  {meta.eyebrow}
                </p>
                <h2 className="font-serif-display text-2xl text-[#1C110C] mt-0.5">
                  {meta.title}
                  {data && (
                    <span className="ml-2 text-sm text-[#7A6B5F] font-sans tracking-normal">
                      · {data.count}
                    </span>
                  )}
                </h2>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              data-testid="portal-kpi-drawer-close"
              aria-label="Close"
              className="text-[#7A6B5F] hover:text-[#1C110C] p-1"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {kind === "responses" && (
            <div className="mb-4 flex items-center justify-between gap-3 flex-wrap bg-white border border-[#D4A963]/40 px-4 py-3">
              <p className="text-[11px] text-[#3E2A22] leading-snug max-w-[360px]">
                Old reports sent before the response-required control can be converted to
                <span className="font-medium"> Sent · Information Only</span>.
              </p>
              <button
                type="button"
                onClick={runCleanup}
                disabled={cleanBusy}
                data-testid="portal-responses-cleanup-btn"
                className="inline-flex items-center gap-2 border border-[#6E1F2C] text-[#6E1F2C] hover:bg-[#6E1F2C] hover:text-[#FAF5EC] px-3 py-2 text-[10px] tracking-[0.24em] uppercase disabled:opacity-60"
              >
                <Trash2 size={11} /> {cleanBusy ? "Cleaning…" : "Clean up old pending items"}
              </button>
              {cleanMsg && <p className="text-[10px] text-[#3F6E2C] w-full" data-testid="portal-responses-cleanup-msg">{cleanMsg}</p>}
            </div>
          )}
          {err ? (
            <div
              data-testid="portal-kpi-drawer-error"
              className="bg-white border border-[#A3141A]/30 text-[#A3141A] p-4 text-sm"
            >
              {err}
            </div>
          ) : !data ? (
            <div data-testid="portal-kpi-drawer-loading" className="space-y-3">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="bg-white border border-[#6E1F2C]/10 h-20 animate-pulse" />
              ))}
            </div>
          ) : data.count === 0 ? (
            <div
              data-testid="portal-kpi-drawer-empty"
              className="bg-white border border-dashed border-[#6E1F2C]/25 p-10 text-center"
            >
              <Icon size={28} className="mx-auto text-[#D4A963] mb-3" />
              <p className="text-sm text-[#3E2A22]">{meta.empty}</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid={`portal-kpi-drawer-list-${kind}`}>
              {data.items.map((it, idx) => (
                <Row key={idx} it={it} onClose={onClose} onChanged={() => setRefreshKey((k) => k + 1)} />
              ))}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}
